import { useInfiniteQuery } from '@tanstack/react-query';
import { format, toZonedTime } from 'date-fns-tz';
import { router } from 'expo-router';
import * as React from 'react';
import { ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { client, type Reservation } from '@/api';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { isDesktop } from '@/core';
import { Button, Text } from '@/ui';
import { Receipts } from '@/ui/icons';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Loader } from '@/ui/loader';
import { MenuItem } from '@/ui/menu-item';

interface Page {
  data: Reservation[];
  meta: {
    page: number;
    totalPages: number;
  };
}

export default function TripHistory() {
  const { styles, theme } = useStyles(stylesheet);

  const fetchTripHistory = async ({ pageParam = 1 }) => {
    return client
      .get(
        `v2/reservations/trip-history?page=${pageParam}&per_page=5&sort=desc`
      )
      .then((response) => response.data);
  };

  const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: ['reservations/trip-history'],
      queryFn: fetchTripHistory,
      initialPageParam: 1,
      getNextPageParam: (lastPage: Page, _pages: Page[]) => {
        if (lastPage.meta.page >= lastPage.meta.totalPages) {
          return undefined;
        }

        return lastPage.meta.page + 1;
      },
    });

  const allReceipts = React.useMemo(() => {
    return data?.pages.map((page) => page.data).flat();
  }, [data]);

  const formatDateRange = (startDate: Date, endDate: Date) => {
    const startFormatted = format(startDate, 'MMMM dd');
    const endFormatted = format(endDate, 'MMMM dd');
    const year = format(startDate, 'yyyy');

    return `${startFormatted} - ${endFormatted}, ${year}`;
  };

  if (isFetching && !isFetchingNextPage) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      style={{
        backgroundColor: theme.colors.background_1,
        paddingBottom: 16,
      }}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
        }}
      >
        <View style={styles.container}>
          {isDesktop() ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.navigate('/account');
                }}
              />

              <Text type="largeTitle">Receipts</Text>
            </View>
          ) : null}

          {allReceipts?.length === 0 ? (
            <View
              style={{
                flexGrow: 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                You don't have any receipts
              </Text>
            </View>
          ) : null}

          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              width: '100%',
              alignItems: 'center',
              gap: 8,
            }}
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={false}
          >
            {allReceipts?.map((trip: Reservation) => (
              <MenuItem
                key={trip.id}
                leftIcon={<Receipts />}
                leftIconFillColor={theme.colors.icon.color}
                rightIcon={<ChevronRight />}
                rightIconFillColor={theme.colors.icon.color}
                tx="tripHistory.city"
                tx2="tripHistory.dateRange"
                values={{ city: trip.pickupLocation?.city }}
                values2={{
                  dateRange: formatDateRange(
                    toZonedTime(trip.startTime, trip.pickupLocation.timezone),
                    toZonedTime(trip.endTime, trip.returnLocation.timezone)
                  ),
                }}
                onPress={() => {
                  router.push(`/account/trip-history/${trip.id}`);
                }}
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: theme.colors.card.default.borderColor,
                }}
              />
            ))}

            {hasNextPage && !isFetchingNextPage && (
              <Button
                tx="tripHistory.showMore"
                type="ghost"
                onPress={fetchNextPage}
              />
            )}

            {isFetchingNextPage && (
              <Loader
                style={{
                  width: 150,
                  height: 150,
                  justifyContent: 'flex-start',
                }}
              />
            )}
          </ScrollView>
        </View>
      </View>
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
}));
